import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { catchError, debounceTime, of, Subject, switchMap, tap } from 'rxjs';
import { BoardCompanyService } from '../../../../services/board-company.service';
import { BoardCompany, NewCardCompany } from '../../board-v3.types';
import {
  customFieldEmailValidator,
  customFieldPhoneValidator,
  normalizePhonePayloadValue,
} from '../../custom-field.validators';
import { formatContactPhone } from '../../contacts/contact.utils';
import { getCompanyEmails, getCompanyName, getCompanyPhones } from '../company.utils';
import { AddressInput } from '../../../../interfaces/card';
import { toAddressInput } from '../../address.utils';

export type CompanyDraftForm = FormGroup<{
  name: FormControl<string>;
  phones: FormArray<FormControl<string>>;
  emails: FormArray<FormControl<string>>;
  addresses: FormControl<AddressInput[]>;
}>;

/**
 * The company of a card: nothing picked, an existing company, a new one (`draft` only)
 * or an existing one being edited (`existing` + `draft`)
 */
export interface CompanyPickerSlot {
  existing: BoardCompany | null;
  draft: CompanyDraftForm | null;
}

export function createEmptyCompanySlot(): CompanyPickerSlot {
  return { existing: null, draft: null };
}

export function createCompanyDraftForm(value: Partial<NewCardCompany> = {}): CompanyDraftForm {
  return new FormGroup({
    name: new FormControl(value.name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    phones: new FormArray((value.phones?.length ? value.phones : ['']).map(createCompanyPhoneControl)),
    emails: new FormArray((value.emails?.length ? value.emails : ['']).map(createCompanyEmailControl)),
    addresses: new FormControl(value.addresses ?? [], { nonNullable: true }),
  });
}

function createCompanyPhoneControl(value = ''): FormControl<string> {
  return new FormControl(value, { nonNullable: true, validators: [customFieldPhoneValidator] });
}

function createCompanyEmailControl(value = ''): FormControl<string> {
  return new FormControl(value, { nonNullable: true, validators: [customFieldEmailValidator] });
}

/** Draft prefilled with an existing company, to edit it */
export function createCompanyEditForm(company: BoardCompany): CompanyDraftForm {
  return createCompanyDraftForm({
    name: company.name,
    phones: getCompanyPhones(company),
    emails: getCompanyEmails(company),
    addresses: (company.addresses ?? []).map(toAddressInput),
  });
}

/** Create payload of a new company; for an edited one it is the update payload, the lists replace the old ones */
export function getCompanyDraftPayload(form: CompanyDraftForm): NewCardCompany {
  const raw = form.getRawValue();

  return {
    name: raw.name.trim(),
    phones: [...new Set(raw.phones.map((phone) => normalizePhonePayloadValue(phone)).filter(Boolean))],
    emails: [...new Set(raw.emails.map((email) => email.trim().toLowerCase()).filter(Boolean))],
    addresses: raw.addresses,
  };
}

/** Puts the search text into the field it looks like: phone, email or name */
function draftFromQuery(query: string): Partial<NewCardCompany> {
  const value = query.trim();
  if (!value) return {};
  if (value.includes('@')) return { emails: [value] };
  if (/^[\d+\s()-]+$/.test(value) && /\d{3,}/.test(value.replace(/\D/g, ''))) return { phones: [value] };

  return { name: value };
}

@Component({
  selector: 'app-board-v3-company-picker',
  templateUrl: './company-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CompanyPickerComponent implements OnInit {
  @Input({ required: true }) slot!: CompanyPickerSlot;
  @Output() slotChange = new EventEmitter<CompanyPickerSlot>();
  @ViewChild('origin') private origin?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly companyService = inject(BoardCompanyService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly search$ = new Subject<string>();

  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];
  protected query = '';
  protected results: BoardCompany[] = [];
  protected searching = false;
  protected open = false;
  protected overlayWidth = 0;

  protected readonly getCompanyName = getCompanyName;

  get draft(): CompanyDraftForm | null {
    return this.slot.draft;
  }

  /** An existing company whose data is being edited */
  get isEditing(): boolean {
    return !!this.slot.existing && !!this.slot.draft;
  }

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(300),
        tap(() => (this.searching = true)),
        switchMap((search) =>
          this.companyService.getCompanies({
            filters: search.split(/\s+/).filter(Boolean).map(value => ({
              key: 'company_details', type: 'ilike', value, is_custom: false,
            })),
            filter_mode: 'AND',
            take: 10,
          }).pipe(catchError(() => of({ items: [] }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.results = response.items;
        this.searching = false;
      });
  }

  protected getCompanySummary(company: BoardCompany): string {
    return [...getCompanyPhones(company).map(formatContactPhone), ...getCompanyEmails(company)].join(', ');
  }

  protected onQueryChange(value: string): void {
    this.query = value;
    if (!value.trim()) {
      this.open = false;
      this.results = [];
      return;
    }

    this.openOverlay();
    this.searching = true;
    this.search$.next(value.trim());
  }

  protected onFocus(): void {
    if (this.query.trim()) this.openOverlay();
  }

  protected onOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.origin?.nativeElement.contains(target)) return;

    this.open = false;
  }

  protected select(company: BoardCompany): void {
    this.reset();
    this.slotChange.emit({ existing: company, draft: null });
  }

  protected createNew(): void {
    const draft = createCompanyDraftForm(draftFromQuery(this.query));
    this.reset();
    this.slotChange.emit({ existing: null, draft });
  }

  protected edit(): void {
    if (!this.slot.existing) return;

    this.slotChange.emit({ existing: this.slot.existing, draft: createCompanyEditForm(this.slot.existing) });
  }

  protected cancelEdit(): void {
    this.slotChange.emit({ existing: this.slot.existing, draft: null });
  }

  protected addPhone(): void {
    this.draft?.controls.phones.push(createCompanyPhoneControl());
  }

  protected addEmail(): void {
    this.draft?.controls.emails.push(createCompanyEmailControl());
  }

  protected removeRow(list: FormArray<FormControl<string>>, index: number): void {
    if (list.length > 1) list.removeAt(index);
    else list.at(0).setValue('');
    list.markAsDirty();
  }

  /** Back to the search to pick another company */
  protected change(): void {
    this.slotChange.emit(createEmptyCompanySlot());
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  private openOverlay(): void {
    this.overlayWidth = this.origin?.nativeElement.getBoundingClientRect().width ?? 0;
    this.open = true;
  }

  private reset(): void {
    this.query = '';
    this.results = [];
    this.open = false;
  }
}
